import React, { useEffect, useRef, useState } from 'react';
import { Combobox, ComboboxInput, ComboboxOption, ComboboxOptions } from '@headlessui/react';
import type { AddressSubValue } from '@repo/schemas';
import {
  createSessionToken,
  fetchPlaceDetails,
  fetchSuggestions,
  loadPlacesLibrary,
  mapPlaceToAddress,
} from '@repo/checkout';
import type { Suggestion } from '@repo/checkout';

import { Input } from '@dashboard/components/ui/input';
import { cn } from '@dashboard/lib/utils';

type Props = {
  id: string;
  value: string;
  onChange: (next: string) => void;
  onPickAddress: (partial: Partial<AddressSubValue>) => void;
  country?: string;
  placeholder?: string;
  ariaLabel?: string;
  required?: boolean;
  className?: string;
};

const DEBOUNCE_MS = 100;

// Required by the Maps Platform branding guidelines whenever Place Autocomplete
// predictions render outside a map. Dashboard forms are always on white, so the
// light-background lockup is the only variant we need.
const POWERED_BY_GOOGLE =
  'https://maps.gstatic.com/mapfiles/api-3/images/powered-by-google-on-white3.png';
const POWERED_BY_GOOGLE_2X =
  'https://maps.gstatic.com/mapfiles/api-3/images/powered-by-google-on-white3_hdpi.png';

type PlacesStatus = 'pending' | 'ready' | 'unavailable';

const AddressAutocompleteInput: React.FC<Props> = ({
  id,
  value,
  onChange,
  onPickAddress,
  country,
  placeholder,
  ariaLabel,
  required,
  className,
}) => {
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const [status, setStatus] = useState<PlacesStatus>('pending');
  const sessionTokenRef = useRef<unknown>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    let cancelled = false;

    loadPlacesLibrary()
      .then(() => {
        if (cancelled) return;
        sessionTokenRef.current = createSessionToken();
        setStatus('ready');
      })
      .catch(() => {
        if (cancelled) return;
        setStatus('unavailable');
      });

    return () => {
      cancelled = true;
      if (debounceRef.current) clearTimeout(debounceRef.current);
      abortRef.current?.abort();
    };
  }, []);

  const cancelPending = () => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = null;
    abortRef.current?.abort();
    abortRef.current = null;
  };

  const requestSuggestions = (input: string) => {
    if (status !== 'ready') return;
    cancelPending();

    const controller = new AbortController();
    abortRef.current = controller;

    debounceRef.current = setTimeout(() => {
      void fetchSuggestions({
        input,
        country,
        sessionToken: sessionTokenRef.current,
        signal: controller.signal,
      })
        .then(result => {
          if (controller.signal.aborted) return;
          setSuggestions(result);
        })
        .catch(() => {
          setSuggestions([]);
        });
    }, DEBOUNCE_MS);
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const next = event.target.value;
    onChange(next);

    // Browser autofill fires input events with no inputType (Chrome) or
    // 'insertReplacementText' (Safari/Firefox); real typing always carries one.
    // Autofill already produced a whole address, so it must not open the search.
    const { inputType } = event.nativeEvent as Partial<InputEvent>;
    if (next.trim() && inputType && inputType !== 'insertReplacementText') {
      requestSuggestions(next);
      return;
    }

    cancelPending();
    setSuggestions([]);
  };

  const handleSelect = async (suggestion: Suggestion | null) => {
    if (!suggestion) return;
    try {
      const place = await fetchPlaceDetails({
        placeId: suggestion.placeId,
        sessionToken: sessionTokenRef.current,
      });
      onPickAddress(mapPlaceToAddress(place));
      // Google bills one session token per "autocomplete then details" exchange,
      // so the next lookup has to start from a fresh one.
      sessionTokenRef.current = createSessionToken();
    } catch {
      // Places is best effort here: the typed text stays, the seller can carry on.
    }
    setSuggestions([]);
  };

  if (status === 'unavailable') {
    return (
      <Input
        id={id}
        placeholder={placeholder}
        aria-label={ariaLabel}
        required={required}
        value={value}
        onChange={event => onChange(event.target.value)}
        className={className}
      />
    );
  }

  return (
    <Combobox<Suggestion | null> value={null} onChange={handleSelect} immediate>
      <div className="relative">
        <ComboboxInput
          as={Input}
          id={id}
          type="text"
          aria-label={ariaLabel}
          placeholder={placeholder}
          required={required}
          autoComplete="off"
          value={value}
          displayValue={() => value}
          onChange={handleChange}
          className={className}
        />

        {suggestions.length > 0 && (
          <ComboboxOptions className="absolute top-full right-0 left-0 z-50 mt-1 overflow-hidden rounded-lg border border-border bg-popover text-popover-foreground shadow-md">
            {/* The attribution sits outside the scroll region so it stays visible
                however many predictions come back. */}
            <div className="max-h-64 overflow-y-auto py-1">
              {suggestions.map(suggestion => (
                <ComboboxOption
                  key={suggestion.placeId}
                  value={suggestion}
                  className={({ focus }) =>
                    cn(
                      'cursor-pointer px-2.5 py-1.5 text-sm',
                      focus && 'bg-accent text-accent-foreground',
                    )
                  }
                >
                  <div>{suggestion.primaryText}</div>
                  {suggestion.secondaryText && (
                    <div className="mt-0.5 text-sm text-muted-foreground">
                      {suggestion.secondaryText}
                    </div>
                  )}
                </ComboboxOption>
              ))}
            </div>
            <div className="flex items-center justify-end border-t border-border px-2.5 py-1.5">
              <img
                src={POWERED_BY_GOOGLE}
                srcSet={`${POWERED_BY_GOOGLE} 1x, ${POWERED_BY_GOOGLE_2X} 2x`}
                alt="Powered by Google"
                height={14}
                className="h-3.5 w-auto"
                loading="lazy"
              />
            </div>
          </ComboboxOptions>
        )}
      </div>
    </Combobox>
  );
};

export default AddressAutocompleteInput;
